import classNames from 'classnames';
import Autoplay, { AutoplayOptionsType } from 'embla-carousel-autoplay';
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react';

import React, { useCallback, useEffect, useState } from 'react';
import styles from './Carousel.module.scss';

export type CarouselProps = React.PropsWithChildren<{
    className?: string;
    style?: any;
    hasAutoplay?: boolean;
    autoplayOptions?: AutoplayOptionsType;
    options?: EmblaOptionsType;
}>;
export function Carousel({
    className,
    autoplayOptions,
    hasAutoplay = !!autoplayOptions,
    options,
    ...props
}: CarouselProps) {
    const emblaPlugins = hasAutoplay ? [Autoplay(autoplayOptions)] : [];
    const [carouselRef, carouselApi] = useEmblaCarousel(options, emblaPlugins);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const slides = React.Children.toArray(props.children);

    const scrollTo = useCallback(
        (index: number) => carouselApi && carouselApi.scrollTo(index),
        [carouselApi],
    );

    const onSelect = useCallback(() => {
        carouselApi && setSelectedIndex(carouselApi.selectedScrollSnap());
    }, [carouselApi]);

    useEffect(() => {
        if (!carouselApi) return;

        onSelect();
        carouselApi.on('reInit', onSelect);
        carouselApi.on('select', onSelect);
    }, [carouselApi, onSelect]);

    return (
        <div className={classNames(styles.carousel, className)} {...props}>
            <div className={styles.viewport} ref={carouselRef}>
                <div className={styles.container}>
                    {slides.map((slide, index) => (
                        <div className={styles.slide} key={`carousel-${index}`}>
                            {slide}
                        </div>
                    ))}
                </div>
            </div>

            <div className={styles.dots}>
                {slides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => scrollTo(index)}
                        aria-label={`See slide number ${index + 1}`}
                        className={classNames(
                            styles.dot,
                            index === selectedIndex && styles.dotSelected,
                        )}
                    />
                ))}
            </div>
        </div>
    );
}
